import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import packageJson from '../../../../../package.json';

export interface SystemInfoDto {
  version: string
  distance: number
  commitHash: string
  isDirty: boolean
  branch: string
  commitDate: string
  environment: string
}

@Component({
  selector: 'pixl-about',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    DatePipe,
    TranslocoModule
  ],
  templateUrl: './about.component.html',
  styleUrl: './about.component.scss'
})
export class AboutComponent {
  private readonly dialogRef = inject(MatDialogRef<AboutComponent>);
  private readonly http = inject(HttpClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translocoService = inject(TranslocoService);

  public readonly systemInfo = signal<SystemInfoDto | null>(null);
  public readonly hasError = signal<boolean>(false);
  public readonly pixlEditorVersion = packageJson.dependencies['@wdidev/pixl-editor']?.replace(/^[\^~]/, '');

  constructor() {
    this.http.get<SystemInfoDto>(`${environment.apiUrl}/system-infos`).subscribe({
      next: (info) => this.systemInfo.set(info),
      error: () => {
        this.hasError.set(true);
        this.showErrorSnackbar(this.translocoService.translate('failed_to_load_system_info'));
      }
    });
  }

  public copySystemInfo(): void {
    const info = this.systemInfo();
    if (!info) {
      return;
    }

    const versionStr = info.distance > 0 ?
      `${info.version} (+${info.distance} ${this.translocoService.translate('commits_ahead')})` :
      info.version;

    const commitStr = info.isDirty ?
      `${info.commitHash} (${this.translocoService.translate('dirty')})` :
      info.commitHash;

    const formattedDate = new Date(info.commitDate).toLocaleString('de-DE');

    const textToCopy = [
      `- ${this.translocoService.translate('version')}: ${versionStr}`,
      `- ${this.translocoService.translate('commit')}: ${commitStr}`,
      `- ${this.translocoService.translate('branch')}: ${info.branch}`,
      `- ${this.translocoService.translate('created_at')}: ${formattedDate}`,
      `- ${this.translocoService.translate('pixl_editor')}: ${this.pixlEditorVersion}`,
      `- ${this.translocoService.translate('user_agent')}: ${navigator.userAgent}`
    ].join('\n');

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).then(() => {
        this.showCopiedSnackbar();
      }).catch(() => {
        this.fallbackCopy(textToCopy);
      });
    }
    else {
      this.fallbackCopy(textToCopy);
    }
  }

  private showCopiedSnackbar(): void {
    this.snackBar.open(this.translocoService.translate('copied_to_clipboard'), '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private showErrorSnackbar(message: string): void {
    this.snackBar.open(message, '', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private fallbackCopy(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showCopiedSnackbar();
      }
      else {
        this.showErrorSnackbar(this.translocoService.translate('copy_to_clipboard_failed'));
      }
    }
    catch {
      this.showErrorSnackbar(this.translocoService.translate('copy_to_clipboard_failed'));
    }
    finally {
      document.body.removeChild(textArea);
    }
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}
